import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Menu,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import { useSearchTargets } from 'hooks/useComponents'
import useLocalStorage from 'hooks/useLocalStorage'
import { usePeriods } from 'hooks/usePeriods'
import type { ChangeEvent, FC } from 'react'
import { useCallback, useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import type { TargetQueryParams, TargetSearchResponse } from 'types/components'
import { createIdLabelMap } from 'types/periods'
import { getOrganizationLabelMap } from 'types/user-profile'
import TargetsTable from '../TargetsTable'

const ManageTargets: FC = () => {
  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const location = useLocation()
  const previousLocationRef = useRef(location.pathname)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'targets-selected-organizations',
    []
  )
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'targets-selected-periods',
    [],
    profile?.user?.id
  )
  const [periodLabelMap, setPeriodLabelMap] = useState<{ [key: string]: string }>({})
  const [organizationLabelMap, setOrganizationLabelMap] = useState<{ [key: string]: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [targets, setTargets] = useState<TargetSearchResponse>({
    targets: [],
    paginationInfo: {
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      perPage: 0,
    },
  })
  const [page, setPage] = useLocalStorage<number>('targets-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('targets-rows-per-page', 25, profile?.user?.id)
  const [sort, setSort] = useLocalStorage<string>('targets-sort', 'label', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('targets-order', 'asc', profile?.user?.id)
  const [isTeamTargets, setIsTeamTargets] = useLocalStorage<boolean>('targets-is-team', true, profile?.user?.id)
  const [showActiveComponents, setShowActiveComponents] = useLocalStorage<boolean>(
    'targets-show-active-components',
    true,
    profile?.user?.id
  )
  const [showArchivedComponents, setShowArchivedComponents] = useLocalStorage<boolean>(
    'targets-show-archived-components',
    false,
    profile?.user?.id
  )
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState<null | HTMLElement>(null)

  const theme = useTheme()
  const { periods, isLoadingPeriods } = usePeriods(token!, rootOrganizationId, rootOrganizationId !== '')

  const queryParams: TargetQueryParams = {
    organizationIds: selectedOrganizations,
    periodIds: selectedPeriods,
    searchTerm,
    pagination: {
      page: page + 1,
      perPage: rowsPerPage,
    },
    sort: [{ field: sort, order }],
    isOrganizationLevel: isTeamTargets,
    isArchived:
      showActiveComponents && !showArchivedComponents
        ? false
        : !showActiveComponents && showArchivedComponents
          ? true
          : undefined,
  }

  const {
    targets: targetsRsp,
    isLoading: isLoadingTargets,
    refetch: refetchTargets,
  } = useSearchTargets(token!, queryParams)

  // Memoized refetch function to prevent unnecessary re-renders
  const stableRefetchTargets = useCallback(() => {
    if (token) {
      refetchTargets()
    }
  }, [refetchTargets, token])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        stableRefetchTargets()
      }
    }

    const handleFocus = () => {
      stableRefetchTargets()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    // Initial data fetch
    stableRefetchTargets()

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [stableRefetchTargets])

  // Refetch when navigating back to this route from other pages
  useEffect(() => {
    const currentPath = location.pathname
    const previousPath = previousLocationRef.current

    // If we're on the targets page and came from a different route, refetch
    if (currentPath === '/admin/components/targets' && previousPath !== currentPath) {
      stableRefetchTargets()
    }

    // Update the previous location ref
    previousLocationRef.current = currentPath
  }, [location.pathname, stableRefetchTargets])

  useEffect(() => {
    setIsLoading(isLoadingTargets || isLoadingPeriods || isLoadingProfile)
  }, [isLoadingTargets, isLoadingPeriods, isLoadingProfile])

  useEffect(() => {
    if (targetsRsp) {
      setTargets(targetsRsp)
    }
  }, [targetsRsp])

  useEffect(() => {
    if (periods) {
      const newPeriodLabelMap =
        periods && periods.periods
          ? periods.periods.reduce((acc: { [key: string]: string }, period) => {
              const idLabelMap = createIdLabelMap(period)
              Object.entries(idLabelMap).forEach(([id, label]) => {
                const newLabel = label.includes(period.label) ? label : `${label} (${period.label})`
                acc[id] = newLabel
              })
              return acc
            }, {})
          : {}

      setPeriodLabelMap(newPeriodLabelMap)
    }
  }, [periods])

  useEffect(() => {
    if (profile) {
      const newOrganizationLabelMap = getOrganizationLabelMap(profile)
      setOrganizationLabelMap(newOrganizationLabelMap)
      // find the first root organization
      const org = profile.roles.find((role) => role.organization.rootOrganizationId != null)
      if (org) {
        setRootOrganizationId(org.organization.rootOrganizationId!)
      }
    }
  }, [profile])

  useEffect(() => {}, [selectedOrganizations, selectedPeriods, searchTerm])

  // Handle sorting
  const handleSort = (property: string) => {
    const isAscending = sort === property && order === 'asc'
    setOrder(isAscending ? 'desc' : 'asc')
    setSort(property)
  }

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handleOrganizationChange = (newOrganizations: string[]) => {
    setSelectedOrganizations(newOrganizations)
    setPage(0)
  }

  const handlePeriodChange = (newPeriods: string[]) => {
    setSelectedPeriods(newPeriods)
    setPage(0)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
    setRowsPerPage(+event?.target?.value!)
    setPage(0)
  }

  const handleTargetTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsTeamTargets(event.target.checked)
    setPage(0)
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 4 }}>
          <SimpleTitle title="Targets" />
        </Grid>
        <Grid size={{ xs: 8 }} />
      </Grid>
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="targets-search-input"
            key="targets-search-input"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={<Switch checked={isTeamTargets} onChange={handleTargetTypeChange} size="medium" />}
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {isTeamTargets ? 'Team' : 'Individual'}
                </Typography>
              }
              labelPlacement="start"
              sx={{ margin: 0 }}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 2 }}>
          <MultiPeriodSelector
            periods={periods != null ? periods.periods : []}
            selectedPeriods={selectedPeriods}
            handleChange={handlePeriodChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <MultiOrganizationSelector
            userProfile={profile!}
            selectedOrganizationIds={selectedOrganizations}
            handleChange={handleOrganizationChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={(e) => setMoreFiltersAnchor(e.currentTarget)}
              startIcon={<FilterOutlined />}
              sx={{ minWidth: 140 }}
            >
              More Filters
            </Button>
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedOrganizations([])
                  setSelectedPeriods([])
                  setSearchTerm('')
                  setShowActiveComponents(true)
                  setShowArchivedComponents(false)
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <TargetsTable
          periodLabelMap={periodLabelMap}
          organizationLabelMap={organizationLabelMap}
          targets={targets}
          page={page}
          rowsPerPage={rowsPerPage}
          sort={sort}
          order={order}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleSort={handleSort}
        />
      )}

      {/* More Filters Menu */}
      <Menu
        anchorEl={moreFiltersAnchor}
        open={Boolean(moreFiltersAnchor)}
        onClose={() => setMoreFiltersAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { minWidth: '280px', mt: 1 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            Components
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveComponents}
                  onChange={(e) => {
                    setShowActiveComponents(e.target.checked)
                    setPage(0)
                  }}
                  size="small"
                />
              }
              label="Show Active Components"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showArchivedComponents}
                  onChange={(e) => {
                    setShowArchivedComponents(e.target.checked)
                    setPage(0)
                  }}
                  size="small"
                />
              }
              label="Show Archived Components"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Stack>
        </Box>
      </Menu>
    </>
  )
}

export default ManageTargets
